import { queryKeys } from '@/lib/query-keys';
import { apiError } from '@/lib/api/ts-rest';
import { organizationClient } from '@/lib/api/ts-rest';
import type {
  Organization,
  OrganizationDeletionImpact,
  OrganizationSwitcherItem,
} from '@octave/contracts';
import { useQuery } from '@tanstack/react-query';
import { useTenantSlug } from './useTenantSlug';

export function useCurrentOrganization() {
  const { tenantSlug } = useTenantSlug();

  return useQuery({
    queryKey: queryKeys.organizations.current(tenantSlug),
    queryFn: async () => {
      const res = await organizationClient.getCurrentOrganization({
        params: { tenantSlug },
      });
      if (res.status !== 200) throw apiError(res, 'Failed to load organization');
      return res.body;
    },
    enabled: !!tenantSlug, // Only fetch when we have a tenant slug
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    retry: false,
  });
}

export const useNotOnboardedOrganizations = () => {
  return useQuery({
    queryKey: queryKeys.organizations.notOnboarded(),
    queryFn: async () => {
      const res = await organizationClient.listNotOnboardedOrganizations();
      if (res.status !== 200) throw apiError(res, 'Failed to load organizations');
      return res.body;
    },
  });
};

export const useOrganizations = () => {
  return useQuery<Organization[]>({
    queryKey: queryKeys.organizations.all(),
    queryFn: async () => {
      const res = await organizationClient.listOrganizations();
      if (res.status !== 200) throw apiError(res, 'Failed to load organizations');
      return res.body;
    },
  });
};

export const useSwitcherOrganizations = () => {
  return useQuery<OrganizationSwitcherItem[]>({
    queryKey: queryKeys.organizations.switcher(),
    queryFn: async () => {
      const res = await organizationClient.listSwitcherOrganizations();
      if (res.status !== 200) throw apiError(res, 'Failed to load organizations');
      return res.body;
    },
  });
};

export const useOrganization = (organizationId: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.organizations.detail(organizationId),
    queryFn: async () => {
      const res = await organizationClient.getOrganization({
        params: { organizationId: organizationId! },
      });
      if (res.status !== 200) throw apiError(res, 'Failed to load organization');
      return res.body;
    },
    enabled: !!organizationId,
    retry: false,
  });
};

/** Counts shown in the archive confirmation dialog. */
export const useOrganizationDeletionImpact = (
  organizationId: string | undefined
) => {
  return useQuery<OrganizationDeletionImpact>({
    queryKey: queryKeys.organizations.deletionImpact(organizationId),
    queryFn: async () => {
      const res = await organizationClient.getDeletionImpact({
        params: { organizationId: organizationId! },
      });
      if (res.status !== 200) throw apiError(res, 'Failed to load impact');
      return res.body;
    },
    enabled: !!organizationId,
    // Always refetched when the dialog opens: the numbers drive a
    // destructive decision, a cached count would be misleading.
    staleTime: 0,
  });
};
